import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { AmexHowFaireWorksListItem as HowItWorksListItem } from "../amex/AmexHowFaireWorksListItem";

export const IncentiveHowFaireWorks: React.FC<{
  backgroundColor: string;
}> = ({ backgroundColor }) => {
  const isSSR = useIsSSR();
  const { strictLocalize } = useStrictLocalization();
  return (
    <Wrapper $backgroundColor={backgroundColor}>
      <ContentWrapper gap={{ desktopAndAbove: "2x", mobileAndAbove: "6x" }}>
        <ActionContainer gap="6x">
          <Typography color={Core.text.default} variant="displaySSerifRegular">
            <StrictLocalMsg
              defaultMessage="Order wholesale products online with Faire. Here’s how it works:"
              description={{
                text: "Header for How it works section for Amex & Square landing page",
                img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
              }}
            />
          </Typography>
          <Column gap={{ mobileAndAbove: "2x", desktopAndAbove: "4x" }}>
            <HowItWorksListItem
              listRank="01"
              title={strictLocalize({
                defaultMessage: "Discover unique products",
                description: {
                  text: "Title for the first of the list",
                  img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
                },
              })}
              body={strictLocalize({
                defaultMessage:
                  "Whether your customers are into eco-friendly apparel or sassy greeting cards, easily stock your shop with unique products from over 10,000 makers.",
                description: {
                  text: "Description for the first of the list",
                  img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
                },
              })}
            />
            <HowItWorksListItem
              listRank="02"
              title={strictLocalize({
                defaultMessage: "Buy with confidence",
                description: {
                  text: "Title for the second of the list",
                  img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
                },
              })}
              body={strictLocalize({
                defaultMessage:
                  "Get free returns on all opening orders with new brands—if something’s not right, we'll cover the cost of shipping products back.",
                description: {
                  text: "Description for the second of the list",
                  img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
                },
              })}
            />
            <HowItWorksListItem
              listRank="03"
              title={strictLocalize({
                defaultMessage: "Buy now, pay later",
                description: {
                  text: "Title for the third of the list",
                  img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
                },
              })}
              body={strictLocalize({
                defaultMessage: "Pay up to 60 days later—interest-free.",
                description: {
                  text: "Description for the third of the list",
                  img: "https://cdn.faire.com/fastly/aadca1a87d37787b67d6435703622af234a8ed01cc06b2fe5e043bd451b7a924.png",
                },
              })}
            />
          </Column>
        </ActionContainer>
        <Column style={{ height: "100%" }}>
          <ActionImage
            src={optimizeImageUrl(
              {
                url: "https://cdn.faire.com/fastly/8ef16d4813a56a75abc9f0fa4315c9115e9cea8a9db42dfd42f9b13cea4d4877.png",
                width: 1000,
              },
              isSSR
            )}
          />
        </Column>
      </ContentWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Column).attrs({
  align: "center",
})<{ $backgroundColor: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("6x", "3x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing("9x", 0)};
    width: 100%;
  }
`;

const ContentWrapper = styled(Flex)
  .attrs({
    align: "center",
    justify: "space-between",
  })
  .attrs({ direction: { mobileAndAbove: "column", desktopAndAbove: "row" } })`
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing(0, "3x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 920px;
  }
`;

const ActionImage = styled.img`
  width: 100%;
  object-fit: cover;
  ${responsiveMediaQueries.tabletAndAbove} {
    margin: 0;
    width: 400px;
  }
`;

const ActionContainer = styled(Column)`
  text-align: left;
  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 477px;
  }
`;
